import { Component } from '@theme/component';

/** Groups merchant headings without cloning or discarding description content. */
class YZProductDescription extends Component {
  #controller;
  #openSections = new Set();

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    this.addEventListener('toggle', this.#rememberOpen, { capture: true, signal: this.#controller.signal });
    this.groupDescription();
  }

  updatedCallback() {
    super.updatedCallback();
    this.groupDescription();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
  }

  /** @param {Event} event - Native disclosure toggle, captured because it does not bubble. */
  #rememberOpen = (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || details.parentElement !== this) return;
    const key = details.dataset.descriptionKey;
    if (!key) return;
    if (details.open) this.#openSections.add(key);
    else this.#openSections.delete(key);
  };

  groupDescription() {
    const fallback = this.querySelector('[data-description-fallback]');
    const source = this.querySelector('[data-description-source]');
    if (!fallback || !source) return;
    const headings = [...source.querySelectorAll('h2,h3')].filter((heading) =>
      heading.textContent.trim() && !source.contains(heading.closest('details,table'))
    );
    const rank = headings.some((heading) => heading.tagName === 'H2') ? 'H2' : 'H3';
    const boundaries = new Set(headings.filter((heading) => heading.tagName === rank));
    // Native fallback also works without a usable heading or before this module loads.
    if (!boundaries.size) {
      this.wrapTables(source);
      return;
    }
    const nodes = [];
    const collect = (parent) => {
      for (const node of [...parent.childNodes]) {
        if (node instanceof HTMLElement && !boundaries.has(node) &&
            [...boundaries].some((heading) => node.contains(heading))) {
          // Preserve anchors on structural containers when their visual wrapper is removed.
          if (node.id) {
            const anchor = document.createElement('span');
            anchor.id = node.id;
            nodes.push(anchor);
          }
          collect(node);
        } else nodes.push(node);
      }
    };
    collect(source);
    const fragment = document.createDocumentFragment();
    let body;
    let index = 0;
    const addGroup = (label, heading) => {
      const details = document.createElement('details');
      details.className = 'yz-product-accordion';
      details.dataset.descriptionKey = `${index++}:${label}`;
      details.open = this.#openSections.has(details.dataset.descriptionKey);
      const summary = document.createElement('summary');
      const title = document.createElement('h2');
      title.className = 'yz-product-accordion__heading';
      // Preserve heading markup and links. Existing rich content remains merchant-owned.
      if (heading) title.append(...heading.childNodes);
      else title.textContent = label;
      summary.append(title);
      body = document.createElement('div');
      body.className = 'yz-product-accordion__body';
      details.append(summary, body);
      fragment.append(details);
      if (heading?.id) details.id = heading.id;
    };
    const pending = [];
    for (const node of nodes) {
      if (boundaries.has(node)) {
        addGroup(node.textContent.trim(), node);
        body.append(...pending.splice(0));
      } else {
        const meaningful = node.nodeType !== Node.COMMENT_NODE &&
          (node.textContent.trim() || (node instanceof HTMLElement &&
            (node.matches('img,video,iframe,svg,audio,hr') || node.querySelector('img,video,iframe,svg,audio,hr'))));
        if (!body && !meaningful) { pending.push(node); continue; }
        if (!body) {
          addGroup(fallback.querySelector('summary').textContent.trim());
          body.append(...pending.splice(0));
        }
        body.append(node);
      }
    }
    if (body) body.append(...pending);
    this.wrapTables(fragment);
    fallback.replaceWith(fragment);
  }

  /** @param {ParentNode} root - Native fallback or enhanced fragment. */
  wrapTables(root) {
    for (const table of root.querySelectorAll('table')) {
      if (table.parentElement?.classList.contains('yz-product-accordion__table')) continue;
      const scroll = document.createElement('div');
      scroll.className = 'yz-product-accordion__table';
      scroll.tabIndex = 0;
      scroll.setAttribute('role', 'region');
      scroll.setAttribute('aria-label', table.closest('details')?.querySelector('summary')?.textContent || 'Table');
      table.before(scroll);
      scroll.append(table);
    }
  }
}
if (!customElements.get('yz-product-description')) customElements.define('yz-product-description', YZProductDescription);
